import { Button, ButtonSet, IconButton, Stack } from '@carbon/react';
import { Close, Filter } from '@carbon/icons-react';
import { useTranslation } from '@pos/i18n';
import { useEffect, type ReactNode } from 'react';
import type { ListFilters } from './useFilters';
import './FilterPanel.scss';

/** The toolbar button that opens and closes the filter panel, with how many filters are applied. */
export function FilterButton({ filters }: { filters: ListFilters }) {
  const { t } = useTranslation();
  return (
    <Button
      kind={filters.count > 0 ? 'secondary' : 'ghost'}
      renderIcon={Filter}
      aria-expanded={filters.open}
      aria-controls={filters.panelId}
      onClick={filters.toggle}
    >
      {filters.count > 0 ? t('admin.filters.buttonCount', { count: filters.count }) : t('admin.filters.button')}
    </Button>
  );
}

/**
 * A list with its filters in a side panel (DESIGN_SYSTEM.md §13): the panel slides over the page from the right at
 * full height, with the filter fields, then *Reset filters* and *Apply filters* at the bottom. Changes apply only
 * with *Apply filters*. Escape or the close button closes it without applying.
 */
export function FilterLayout({
  filters,
  fields,
  children,
}: {
  filters: ListFilters;
  fields: ReactNode;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const { open, close } = filters;

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  return (
    <>
      {children}
      {open && (
        <aside id={filters.panelId} className="pos-filter-panel" aria-label={t('admin.filters.title')}>
          <div className="pos-filter-panel__header">
            <h2 className="pos-filter-panel__title">{t('admin.filters.title')}</h2>
            <IconButton kind="ghost" label={t('admin.filters.close')} onClick={close}>
              <Close />
            </IconButton>
          </div>
          <form
            noValidate
            className="pos-filter-panel__form"
            onSubmit={(e) => {
              e.preventDefault();
              filters.apply();
            }}
          >
            <Stack gap={5} className="pos-filter-panel__body">
              {fields}
            </Stack>
            <ButtonSet className="pos-filter-panel__footer">
              <Button kind="secondary" disabled={!filters.changed} onClick={filters.reset}>
                {t('admin.filters.reset')}
              </Button>
              <Button kind="primary" type="submit" disabled={!filters.dirty}>
                {t('admin.filters.apply')}
              </Button>
            </ButtonSet>
          </form>
        </aside>
      )}
    </>
  );
}
